import BASE_URL from "../Config";
import { store } from "../store";
import { updateAccessToken, updateRefreshToken, logout } from "../store/authSlice";
import {
  getRefreshToken,
  getEmployeeRefreshToken,
  setEmployeeAccessToken,
  setEmployeeRefreshToken,
  removeEmployeeAccessToken,
  removeEmployeeRefreshToken,
  getFreelanceRefreshToken,
  setFreelanceAccessToken,
  setFreelanceRefreshToken,
  removeFreelanceAccessToken,
  removeFreelanceRefreshToken,
} from "./cookieUtils";

let refreshTokenInterval: NodeJS.Timeout | null = null;
let pendingRefresh: Promise<boolean> | null = null;
let pendingEmployeeRefresh: Promise<boolean> | null = null;
let pendingFreelanceRefresh: Promise<boolean> | null = null;
export const refreshAccessToken = async (): Promise<boolean> => {
  // Return the in-flight promise to prevent concurrent refresh calls
  if (pendingRefresh) return pendingRefresh;

  const refreshToken = getRefreshToken();
  console.log("Initiating token refresh...", { refreshToken: !!refreshToken });
  if (!refreshToken) return false;

  pendingRefresh = (async () => {
    try {
      console.log("Attempting token refresh...");
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) throw new Error(`Refresh failed: ${response.status}`);

      const data = await response.json();

      if (data.mobileNumber) {
        store.dispatch(updateAccessToken(data.mobileNumber));
      }

      if (data.mobileOtpSession) {
        store.dispatch(updateRefreshToken(data.mobileOtpSession));
      }

      return true;
    } catch {
      store.dispatch(logout());
      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      window.location.href = "/whatsapplogin";
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();

  return pendingRefresh;
};
export const refreshEmployeeAccessToken = async (): Promise<boolean> => {
  if (pendingEmployeeRefresh) return pendingEmployeeRefresh;

  const refreshToken = getEmployeeRefreshToken();

  if (!refreshToken) return false;

  pendingEmployeeRefresh = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok)
        throw new Error(`Employee refresh failed: ${response.status}`);

      const data = await response.json();

      if (data.mobileNumber) {
        setEmployeeAccessToken(data.mobileNumber);
      }

      if (data.mobileOtpSession) {
        setEmployeeRefreshToken(data.mobileOtpSession);
      }

      return true;
    } catch (error) {
      removeEmployeeAccessToken();
      removeEmployeeRefreshToken();

      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      window.location.href = "/userlogin";

      return false;
    } finally {
      pendingEmployeeRefresh = null;
    }
  })();

  return pendingEmployeeRefresh;
};

export const refreshFreelanceAccessToken = async (): Promise<boolean> => {
  if (pendingFreelanceRefresh) return pendingFreelanceRefresh;

  const refreshToken = getFreelanceRefreshToken();

  if (!refreshToken) return false;

  pendingFreelanceRefresh = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok)
        throw new Error(`Freelance refresh failed: ${response.status}`);

      const data = await response.json();

      if (data.mobileNumber) {
        setFreelanceAccessToken(data.mobileNumber);
      }

      if (data.mobileOtpSession) {
        setFreelanceRefreshToken(data.mobileOtpSession);
      }

      return true;
    } catch (error) {
      removeFreelanceAccessToken();
      removeFreelanceRefreshToken();
      sessionStorage.removeItem("userId");
      sessionStorage.removeItem("Name");
      sessionStorage.removeItem("primaryType");

      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      window.location.href = "/employee-login";

      return false;
    } finally {
      pendingFreelanceRefresh = null;
    }
  })();

  return pendingFreelanceRefresh;
};

export const startTokenRefresh = (): void => {
  stopTokenRefresh();
  if (!getRefreshToken()) return;

  refreshAccessToken(); // immediate call on start

  refreshTokenInterval = setInterval(() => {
    refreshAccessToken();
  }, 5 * 60 * 1000); // every 5 minutes
};

export const stopTokenRefresh = (): void => {
  if (refreshTokenInterval) {
    clearInterval(refreshTokenInterval);
    refreshTokenInterval = null;
  }
};

export const isAuthenticated = (): boolean =>
  !!(store.getState().auth.accessToken && getRefreshToken());
